"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.groupMeasuresSchema = exports.groupMeasureSchema = exports.groupMeasureStratumSchema = void 0;
// Wire shapes of Screen 2's route (SPEC §7.3, §9 `/api/groups/:id/measures`; J-036), served at
// `/api/v1/performance-groups/:groupId/measures` (D-973). Numbers are raw (SPEC §9: "All responses
// carry numbers unformatted"); the web formats them with `format.ts`. Every figure is an aggregate
// count or rate over a group's patients, never a patient's own value.
const zod_1 = require("zod");
const groups_js_1 = require("./groups.js");
const scoring_js_1 = require("../scoring.js");
/** One stratum row, from `rpt.v_stratum_rows` (SPEC §6.2): the parent's Total, Complete, Exceptions. */
exports.groupMeasureStratumSchema = zod_1.z
    .object({
    stratumKey: zod_1.z.number().int(),
    stratumCode: zod_1.z.string(),
    label: zod_1.z.string(),
    isInverse: zod_1.z.boolean(),
    totalInstances: zod_1.z.number().int(),
    completeInstances: zod_1.z.number().int(),
    exceptions: zod_1.z.number().int(),
    /** Patients flagged with the stratum among the MET and NOT_MET ones. */
    met: zod_1.z.number().int(),
    /** (complete − exceptions) − met. */
    notMet: zod_1.z.number().int(),
    /** 100 × met ÷ (complete − exceptions), unrounded; null when the divisor is 0. */
    performanceRate: zod_1.z.number().nullable(),
})
    .meta({ id: "AcoGroupMeasureStratum" });
/** One measure row, from `core.group_measure_score` and `ref.measure` (SPEC §6.1, §6.3). */
exports.groupMeasureSchema = zod_1.z
    .object({
    /** `ref.measure.measure_key`, the id Screen 4's drill-down links carry. */
    measureKey: zod_1.z.number().int(),
    measureNumber: zod_1.z.string(),
    /** `ref.measure.display_number`, e.g. `#113`. */
    displayNumber: zod_1.z.string(),
    title: zod_1.z.string(),
    isInverse: zod_1.z.boolean(),
    totalInstances: zod_1.z.number().int(),
    completeInstances: zod_1.z.number().int(),
    exceptions: zod_1.z.number().int(),
    met: zod_1.z.number().int(),
    notMet: zod_1.z.number().int(),
    excluded: zod_1.z.number().int(),
    /** 100 × complete ÷ total, as `calculate` stored it; null when total is 0. */
    reportingRate: zod_1.z.number().nullable(),
    /** 100 × (complete + excluded) ÷ (total + excluded), the `CMS` completeness basis. */
    cmsDataCompleteness: zod_1.z.number().nullable(),
    /** 100 × met ÷ (complete − exceptions); null when the divisor is 0. */
    performanceRate: zod_1.z.number().nullable(),
    decile: zod_1.z.number().int().nullable(),
    /** Null when the ruleset excludes the measure from the quality total. */
    points: zod_1.z.number().nullable(),
    scoringStatus: zod_1.z.enum(scoring_js_1.SCORING_STATUSES),
    /** Empty: a year's scores are its bundle's, and the Analysis rules compute no strata (Q-5, J-185). */
    strata: zod_1.z.array(exports.groupMeasureStratumSchema),
})
    .meta({ id: "AcoGroupMeasure" });
/** Screen 2: the header's data and the group's measures in `display_order` (SPEC §7.3). */
exports.groupMeasuresSchema = zod_1.z
    .object({
    groupId: zod_1.z.number().int(),
    performanceYear: zod_1.z.number().int(),
    groupName: zod_1.z.string(),
    /** With `platformName`, the organization line: `{aco_code} {platform_name}` (SPEC §7.2). */
    acoCode: zod_1.z.string(),
    /** J-268: every ACO the group has a practice in that the caller may see; more than one: the filter. */
    acoCodes: zod_1.z.array(zod_1.z.string()),
    /** J-268: the ACO the figures are for, or null for the group's figures over all its practices. */
    figuresAco: zod_1.z.string().nullable(),
    /** J-268: the group's practices in the caller's scope, each with its TIN, name and ACO. */
    practices: zod_1.z.array(groups_js_1.groupPracticeSchema),
    platformName: zod_1.z.string().nullable(),
    /** The header's quality figure, the same totals as the group's Screen 1 row. */
    qualityEarned: zod_1.z.number(),
    qualityMax: zod_1.z.number(),
    /** `patient_match_fields` in order, as field names (`first_name`); the web labels them. */
    patientMatchFields: zod_1.z.array(zod_1.z.string()),
    /** `data_completeness_threshold`: a reporting rate below it is highlighted. */
    completenessThreshold: zod_1.z.number(),
    measures: zod_1.z.array(exports.groupMeasureSchema),
})
    .meta({ id: "AcoGroupMeasures" });
//# sourceMappingURL=measures.js.map